// =============================================================================
// Lightweight toast notifications.
//
// A toast is a small, non-blocking message that slides up from the bottom of
// the viewport and disappears on its own after a few seconds. Use it for
// passive feedback ("Saved", "Synced", "Joined Jana's calendar") that
// shouldn't interrupt what the user is doing.
//
// Usage:
//   const toast = useToast();
//   toast.success('Synchronisiert');
//   toast.error('Fehler beim Synchronisieren');
//   toast.info('Eingeladen');
//   // Or with options:
//   toast({ message: 'Saved', kind: 'success', duration: 1500 });
//
// Wrap the app once with <ToastProvider>. Toasts auto-stack vertically; the
// container respects the iOS safe-area-inset-bottom and sits above the
// Calendar's bottom toggle.
// =============================================================================
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Check, AlertCircle, Info, X } from 'lucide-react';
import { haptics } from './haptics';

const ToastCtx = createContext(null);

export const useToast = () => {
  const api = useContext(ToastCtx);
  if (!api) throw new Error('useToast must be used inside <ToastProvider>');
  return api;
};

const KIND_STYLES = {
  success: {
    icon: Check,
    bg: 'bg-emerald-600',
    ring: 'ring-emerald-500/30'
  },
  error: {
    icon: AlertCircle,
    bg: 'bg-red-600',
    ring: 'ring-red-500/30'
  },
  info: {
    icon: Info,
    bg: 'bg-gray-900 dark:bg-gray-700',
    ring: 'ring-gray-500/30'
  }
};

let nextId = 1;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((curr) => curr.filter((t) => t.id !== id));
    const tm = timersRef.current.get(id);
    if (tm) {
      clearTimeout(tm);
      timersRef.current.delete(id);
    }
  }, []);

  const show = useCallback(
    (input) => {
      const opts =
        typeof input === 'string' ? { message: input } : input || {};
      const id = nextId++;
      const toast = {
        id,
        message: opts.message || '',
        kind: opts.kind || 'info',
        duration: opts.duration ?? (opts.kind === 'error' ? 4500 : 2500)
      };
      setToasts((curr) => [...curr, toast]);
      // Subtle haptic so the toast registers without the user looking down.
      if (toast.kind === 'error') haptics.warning();
      else if (toast.kind === 'success') haptics.success();
      else haptics.tap();
      const tm = setTimeout(() => dismiss(id), toast.duration);
      timersRef.current.set(id, tm);
      return id;
    },
    [dismiss]
  );

  // Clear all timers on unmount to avoid setState-after-unmount warnings.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const tm of timers.values()) clearTimeout(tm);
      timers.clear();
    };
  }, []);

  const api = useMemo(() => {
    const fn = (input) => show(input);
    fn.show = show;
    fn.dismiss = dismiss;
    fn.success = (message, opts) => show({ ...(opts || {}), message, kind: 'success' });
    fn.error = (message, opts) => show({ ...(opts || {}), message, kind: 'error' });
    fn.info = (message, opts) => show({ ...(opts || {}), message, kind: 'info' });
    return fn;
  }, [show, dismiss]);

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastCtx.Provider>
  );
};

const ToastViewport = ({ toasts, onDismiss }) => {
  if (toasts.length === 0) return null;
  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-[100] flex flex-col items-center gap-2 px-4 w-full max-w-sm pointer-events-none"
      style={{
        // Sit above the iOS home indicator AND above the Calendar's bottom
        // toggle pill (~3rem tall + 0.75rem inset). Safe to overlay other
        // pages: their content has its own bottom padding.
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 5rem)'
      }}
      role="status"
      aria-live="polite"
    >
      {toasts.map((t) => {
        const style = KIND_STYLES[t.kind] || KIND_STYLES.info;
        const Icon = style.icon;
        return (
          <div
            key={t.id}
            className={`pointer-events-auto w-full ${style.bg} text-white rounded-full shadow-lg ring-1 ${style.ring} flex items-center gap-2 pl-3 pr-2 py-2 animate-toast-in`}
          >
            <Icon size={16} className="flex-shrink-0 opacity-90" />
            <span className="text-sm font-medium flex-1 truncate">
              {t.message}
            </span>
            <button
              type="button"
              onClick={() => onDismiss(t.id)}
              className="p-1 rounded-full hover:bg-white/15 transition-colors flex-shrink-0"
              aria-label="Dismiss"
            >
              <X size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
};
